import type { BiomassDataRow } from './productionUnitTable'

import { deathsAfterCount } from './countDayDeaths'

export type DisplayedEndOfDayStockSource = 'stored' | 'derived' | 'unknown'

export type DisplayedEndOfDayStock = {
  source: DisplayedEndOfDayStockSource
  value: string
  numericValue: number | null
}

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const toCalendarDayKey = (value: unknown): string => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  // Use the leading YYYY-MM-DD when present; otherwise fall back to Date parsing.
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed)
  if (isoMatch) return isoMatch[1]
  const parsed = Date.parse(trimmed)
  if (!Number.isFinite(parsed)) return ''
  const d = new Date(parsed)
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const resolveGroupKey = (row: BiomassDataRow): string => {
  const lotId = String(row.lotId ?? '').trim()
  if (lotId) return `lot-id:${lotId}`
  const lotKey = String(row.lot ?? '').trim()
  if (lotKey) return `lot-key:${lotKey.toLowerCase()}`
  return ''
}

const compareByDateAscThenId = (left: BiomassDataRow, right: BiomassDataRow): number => {
  const leftTime = Date.parse(String(left.date ?? ''))
  const rightTime = Date.parse(String(right.date ?? ''))
  const leftValid = Number.isFinite(leftTime)
  const rightValid = Number.isFinite(rightTime)

  if (leftValid && rightValid && leftTime !== rightTime) return leftTime - rightTime
  if (leftValid && !rightValid) return -1
  if (!leftValid && rightValid) return 1

  return String(left.id ?? '').localeCompare(String(right.id ?? ''))
}

const formatNumericValue = (value: number): string => {
  if (Number.isInteger(value)) return value.toString()
  return value.toString()
}

/**
 * The end-of-day stock of a row that holds a count. Findings #203: when the
 * count and the day's deaths carry their times, the count is the stock when it
 * was taken and only the deaths recorded after it come off — the same figure
 * whichever rows a screen loads. Rows without times keep the day rule below.
 */
export const endOfDayStockFromCount = (
  row: BiomassDataRow,
  storedStock: number,
  isReceivingEvent: boolean
): number => {
  const after = deathsAfterCount(row)
  if (after !== null) return Math.max(0, storedStock - after)
  const dailyMortality = isReceivingEvent ? parseNumeric(row.dailyMortality) : null
  return dailyMortality !== null && dailyMortality > 0 ? Math.max(0, storedStock - dailyMortality) : storedStock
}

export const computeDisplayedEndOfDayStockByRowId = (
  rows: ReadonlyArray<BiomassDataRow>
): Map<string, DisplayedEndOfDayStock> => {
  const result = new Map<string, DisplayedEndOfDayStock>()
  if (!Array.isArray(rows) || rows.length === 0) return result

  const grouped = new Map<string, BiomassDataRow[]>()
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id) continue
    const key = resolveGroupKey(row)
    if (!key) {
      result.set(row.id, { source: 'unknown', value: '', numericValue: null })
      continue
    }
    const bucket = grouped.get(key)
    if (bucket) {
      bucket.push(row)
    } else {
      grouped.set(key, [row])
    }
  }

  grouped.forEach((groupRows) => {
    const sorted = [...groupRows].sort(compareByDateAscThenId)
    let lastKnownStock: number | null = null
    // Track the calendar day currently being processed so we deduct the day's
    // mortality only once even when multiple rows exist for the same (lot, day).
    // After v2.1 MORT-12 every row for a (lot, day) carries the same canonical
    // dailyMortality (the rollup sum), so without per-day deduplication we would
    // subtract the same mortality once per row and underreport stock.
    let currentDayKey: string | null = null
    let dayMortalityApplied = false

    for (const row of sorted) {
      const rowDayKey = toCalendarDayKey(row.date)
      if (rowDayKey !== currentDayKey) {
        currentDayKey = rowDayKey
        dayMortalityApplied = false
      }

      const storedStock = parseNumeric(row.fishStock)
      if (storedStock !== null) {
        // A stored stock value supersedes any pending mortality deduction for
        // the day. With times (findings #203) only the deaths after the count
        // come off. Without times, the old day rule: a recount already excludes
        // that day's dead, but a lot's very first stock event (`lastKnownStock`
        // still `null`) is a received count taken before that day's losses, so
        // they are netted out (v2.2 findings.md #32).
        const isReceivingEvent: boolean = lastKnownStock === null && !dayMortalityApplied
        const effectiveStock: number = endOfDayStockFromCount(row, storedStock, isReceivingEvent)

        result.set(row.id, {
          source: 'stored',
          value: effectiveStock === storedStock && typeof row.fishStock === 'string' && row.fishStock.trim()
            ? row.fishStock
            : formatNumericValue(effectiveStock),
          numericValue: effectiveStock
        })
        lastKnownStock = effectiveStock
        dayMortalityApplied = true
        continue
      }

      const dailyMortality = parseNumeric(row.dailyMortality)
      if (lastKnownStock !== null && dailyMortality !== null && dailyMortality >= 0) {
        const deduction = dayMortalityApplied ? 0 : dailyMortality
        const derived = Math.max(0, lastKnownStock - deduction)
        result.set(row.id, {
          source: 'derived',
          value: formatNumericValue(derived),
          numericValue: derived
        })
        lastKnownStock = derived
        dayMortalityApplied = true
        continue
      }

      result.set(row.id, { source: 'unknown', value: '', numericValue: null })
    }
  })

  return result
}
